import imageCompression from "browser-image-compression";
import heic2any from "heic2any";

export const PROFILE_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
export const KYC_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// Friendly, user-facing messages
export const MSG = {
  photoTooLarge:
    "Photo is too large. Please upload a photo under 2 MB. Tip: Use your phone's native camera app and select medium quality when saving.",
  docTooLarge:
    "Document is too large. Please upload a file under 10 MB. Try scanning at medium resolution or saving as PDF.",
  photoBadType: "This file type is not supported. Please upload a JPG, PNG, or WEBP photo.",
  docBadType: "This file type is not supported. Please upload a JPG, PNG, or PDF.",
  timeout: "Upload timed out. Please check your internet connection and try again.",
  network: "Upload timed out. Please check your internet connection and try again.",
  processing: "We couldn't process this photo. Please try a different one.",
};

const isHeic = (file) => {
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();
  return t.includes("heic") || t.includes("heif") || n.endsWith(".heic") || n.endsWith(".heif");
};

// Converts iPhone HEIC/HEIF to a JPEG File. Returns original file if not HEIC.
export const normalizeHeic = async (file) => {
  if (!isHeic(file)) return file;
  const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
  const out = Array.isArray(blob) ? blob[0] : blob;
  const name = (file.name || "photo").replace(/\.(heic|heif)$/i, ".jpg");
  return new File([out], name, { type: "image/jpeg" });
};

// Validate a profile photo. Allows JPG/PNG/WEBP + HEIC (converted upstream).
export const isValidProfileType = (file) => {
  const t = (file.type || "").toLowerCase();
  return (
    t === "image/jpeg" ||
    t === "image/png" ||
    t === "image/webp" ||
    isHeic(file)
  );
};

export const isValidKycType = (file) => {
  const t = (file.type || "").toLowerCase();
  return t === "image/jpeg" || t === "image/png" || t === "application/pdf";
};

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("read-failed"));
    r.readAsDataURL(file);
  });

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.src = url;
  });

// Crops the source image to the selected pixel area and outputs a square
// 400x400 JPEG blob. Then compresses to stay comfortably under 2 MB.
export const getCroppedProfileBlob = async (imageSrc, croppedAreaPixels) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const size = 400;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    size,
    size
  );

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9)
  );
  if (!blob) throw new Error("processing");

  // 400x400 JPEG is tiny, but guarantee < 2 MB as a safety net.
  if (blob.size <= PROFILE_MAX_BYTES) {
    return new File([blob], "profile.jpg", { type: "image/jpeg" });
  }
  const compressed = await imageCompression(
    new File([blob], "profile.jpg", { type: "image/jpeg" }),
    { maxSizeMB: 2, maxWidthOrHeight: 400, useWebWorker: true }
  );
  return new File([compressed], "profile.jpg", { type: "image/jpeg" });
};

// Maps any thrown/axios error to a friendly message.
export const mapUploadError = (err, { doc = false } = {}) => {
  if (err?.message === "processing" || err?.message === "read-failed") return MSG.processing;
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) return MSG.timeout;
  if (err?.message === "Network Error" || !err?.response) return MSG.network;
  if (err?.response?.data?.detail) return err.response.data.detail;
  return doc ? MSG.docTooLarge : MSG.photoTooLarge;
};
